import {
  MEASUREMENT_TECHNIQUE,
  TARGET_RESPONSE,
  NON_TARGET_RESPONSE,
  NEW_LESION_RESPONSE,
  CLINICAL_STATUS,
  OVERALL_RESPONSE,
} from './guideline';
import { math } from '../index';
const { round } = math;

function a(num) {
  return Number(num) > 0 ? `+${num}` : num;
}

function buildMessage(label, b, f) {
  const b_sum = b.summation.sumDiameter;
  const f_sum = f.summation.sumDiameter;
  const absolute = round(f_sum - b_sum, -2);
  const change = b_sum === 0 ? 100 : round((100 * (f_sum - b_sum)) / b_sum, -2);
  const number = `${a(change)}% change (${a(absolute)} mm)`;
  return `${label}: ${number} from ${b.id} to ${f.id}`;
}

export function getImmuneTargetResponse(stats, criteria) {
  if (criteria.measurementTechnique !== MEASUREMENT_TECHNIQUE.BIDIMENSIONAL) {
    return {
      result: TARGET_RESPONSE.NOT_EVALUABLE,
      message: 'Target Lesion(NE)',
    };
  }
  if (stats.length < 2) {
    return {
      result: TARGET_RESPONSE.NOT_EVALUABLE,
      message: 'Target Lesion(NE)',
    };
  }
  const baseline_index = 0;
  const baseline = stats[baseline_index];
  const baseline_sum = baseline.summation.sumDiameter;

  const current_index = stats.length - 1;
  const current = stats[current_index];
  const current_sum = current.summation.sumDiameter;

  if (!current.existLesions) {
    return {
      result: TARGET_RESPONSE.NONE,
      message: 'Target Lesion(NED): no target lesion selected',
    };
  }

  let previous_index = current_index - 1;
  while (stats[previous_index].target_response === TARGET_RESPONSE.iCPD) {
    previous_index += -1;
  }
  const previous = stats[previous_index];
  const previous_sum = previous.summation.sumDiameter;
  if (
    previous.target_response === TARGET_RESPONSE.iUPD &&
    current_sum - previous_sum >= 5
  ) {
    const diameter_change = round(current_sum - previous_sum, -2);
    return {
      result: TARGET_RESPONSE.iCPD,
      message:
        `Target Lesion(iCPD): ${a(diameter_change)} mm change` +
        ` from ${previous.id} to ${current.id}`,
    };
  }

  const nadir_index = stats.reduce((index, cur, cur_index, array) => {
    return cur.summation.sumDiameter < array[index].summation.sumDiameter
      ? cur_index
      : index;
  }, 0);
  const nadir = stats[nadir_index];
  const nadir_sum = nadir.summation.sumDiameter;

  if (current_sum >= nadir_sum * 1.2 && current_sum - nadir_sum >= 5) {
    return {
      result: TARGET_RESPONSE.iUPD,
      message: buildMessage('Target Lesion(iUPD)', nadir, current),
    };
  } else if (current_sum === 0) {
    return {
      result: TARGET_RESPONSE.iCR,
      message: `Target Lesion(iCR): no lesion found at ${current.id}`,
    };
  } else if (current_sum <= baseline_sum * 0.7) {
    return {
      result: TARGET_RESPONSE.iPR,
      message: buildMessage('Target Lesion(iPR)', baseline, current),
    };
  } else {
    return {
      result: TARGET_RESPONSE.iSD,
      message: buildMessage('Target Lesion(iSD)', baseline, current),
    };
  }
}

export function getImmuneNonTargetResponse(stats, criteria) {
  if (criteria.measurementTechnique !== MEASUREMENT_TECHNIQUE.BIDIMENSIONAL) {
    return {
      result: NON_TARGET_RESPONSE.NOT_EVALUABLE,
      message: 'Non-target Lesion(NE)',
    };
  }
  if (stats.length < 2) {
    return {
      result: NON_TARGET_RESPONSE.NOT_EVALUABLE,
      message: 'Non-target Lesion(NE)',
    };
  }

  const current_index = stats.length - 1;
  const current = stats[current_index];
  if (current.existROIs) {
    return {
      result: NON_TARGET_RESPONSE.iNN,
      message: `Non-target Lesion(Non-iCR/Non-iUPD): lesion found at ${current.id}; please refer to the DICOM images`,
    };
  }
  if (current.existLesions) {
    return {
      result: NON_TARGET_RESPONSE.iCR,
      message: `Non-target Lesion(iCR): no lesion found at ${current.id}`,
    };
  }
  return {
    result: NON_TARGET_RESPONSE.NONE,
    message: 'Non-target Lesion(NED): no non-target lesion selected',
  };
}

export function getImmuneNewLesionTargetResponse(stats, criteria) {
  if (criteria.measurementTechnique !== MEASUREMENT_TECHNIQUE.BIDIMENSIONAL) {
    return {
      result: NEW_LESION_RESPONSE.NOT_EVALUABLE,
      message: 'New Lesion(NE)',
    };
  }
  if (stats.length < 2) {
    return {
      result: NEW_LESION_RESPONSE.NOT_EVALUABLE,
      message: 'New Lesion(NE)',
    };
  }

  const current_index = stats.length - 1;
  const current = stats[current_index];
  const current_sum = current.summation.sumDiameter;
  const current_lesions = current.Lesions;

  let previous_index = current_index - 1;
  while (
    stats[previous_index].new_lesion_target_response ===
    NEW_LESION_RESPONSE.iCPD
  ) {
    previous_index += -1;
  }
  const previous = stats[previous_index];
  const previous_sum = previous.summation.sumDiameter;
  const previous_lesions = Object.entries(
    stats.reduce((acc, cur, cur_index, array) => {
      if (cur_index === array.length - 1) return acc;
      for (const lesion of cur.Lesions) {
        acc[lesion.id] = acc[lesion.id] || 0;
        acc[lesion.id] += lesion.maxDiameterValue;
      }
      return acc;
    }, {})
  ).map(([key, value]) => ({ id: key, diameter: value }));
  const exist_previous_lesion = previous_lesions.some(
    (lesion) => lesion.diameter > 0
  );
  const exist_new_lesion = current_lesions.some(
    (lesion) => lesion.maxDiameterValue > 0
  );

  if (
    previous.new_lesion_target_response === NEW_LESION_RESPONSE.iPR ||
    previous.new_lesion_target_response === NEW_LESION_RESPONSE.iSD
  ) {
    let baseline_index = current_index - 1;
    let nadir_index = current_index - 1;
    let nadir_sum = stats[nadir_index].summation.sumDiameter;
    while (
      baseline_index > 0 &&
      stats[baseline_index].new_lesion_target_response !==
        NEW_LESION_RESPONSE.iUPD
    ) {
      baseline_index += -1;
      const baseline_sum = stats[baseline_index].summation.sumDiameter;
      if (nadir_sum > baseline_sum) {
        nadir_index = baseline_index;
        nadir_sum = baseline_sum;
      }
    }
    const baseline = stats[baseline_index];
    const baseline_sum = baseline.summation.sumDiameter;
    const nadir = stats[nadir_index];
    if (current_sum >= nadir_sum * 1.2) {
      return {
        result: TARGET_RESPONSE.iUPD,
        message: buildMessage('New Lesion(iUPD)', nadir, current),
      };
    } else if (current_sum > baseline_sum * 0.7) {
      return {
        result: NEW_LESION_RESPONSE.iSD,
        message: buildMessage('New Lesion(iSD)', baseline, current),
      };
    } else {
      return {
        result: NEW_LESION_RESPONSE.iPR,
        message: buildMessage('New Lesion(iPR)', baseline, current),
      };
    }
  }
  if (previous.new_lesion_target_response === NEW_LESION_RESPONSE.iUPD) {
    const exist_extra = current_lesions.some((lesion, index) => {
      const previous_lesion = previous_lesions[index];
      return lesion.maxDiameterValue > 0 && previous_lesion?.diameter === 0;
    });
    if (exist_extra) {
      return {
        result: NEW_LESION_RESPONSE.iCPD,
        message: `New Lesion(iCPD): additional new lesion found at ${current.id}`,
      };
    }
    if (current_sum - previous_sum >= 5) {
      const diameter_change = round(current_sum - previous_sum, -2);
      return {
        result: NEW_LESION_RESPONSE.iCPD,
        message:
          `New Lesion(iCPD):` +
          ` target new lesions ${a(diameter_change)} mm change` +
          ` from ${previous.id} to ${current.id}`,
      };
    }
    if (exist_new_lesion) {
      const diameter_change = round(current_sum - previous_sum, -2);
      if (current_sum >= previous_sum * 1.2) {
        return {
          result: NEW_LESION_RESPONSE.iUPD,
          message:
            `New Lesion(iUPD):` +
            ` target new lesions ${a(diameter_change)} mm change` +
            ` from ${previous.id} to ${current.id}`,
        };
      } else if (current_sum > previous_sum * 0.7) {
        return {
          result: NEW_LESION_RESPONSE.iSD,
          message:
            `New Lesion(iSD):` +
            ` target new lesions ${a(diameter_change)} mm change` +
            ` from ${previous.id} to ${current.id}`,
        };
      } else {
        return {
          result: NEW_LESION_RESPONSE.iPR,
          message:
            `New Lesion(iPR):` +
            ` target new lesions ${a(diameter_change)} mm change` +
            ` from ${previous.id} to ${current.id}`,
        };
      }
    }
  }

  if (exist_new_lesion) {
    return {
      result: NEW_LESION_RESPONSE.iUPD,
      message: `New Lesion(iUPD): new lesion found at ${current.id}`,
    };
  }

  if (exist_previous_lesion) {
    return {
      result: NEW_LESION_RESPONSE.iCR,
      message: `New Lesion(iCR): no new lesion found at ${current.id}`,
    };
  }

  return {
    result: NEW_LESION_RESPONSE.ABSENT,
    message: 'New Lesion(Absent): no new lesion found',
  };
}

export function getImmuneNewLesionNonTargetResponse(stats, criteria) {
  if (criteria.measurementTechnique !== MEASUREMENT_TECHNIQUE.BIDIMENSIONAL) {
    return {
      result: NEW_LESION_RESPONSE.NOT_EVALUABLE,
      message: 'New Lesion(NE)',
    };
  }
  if (stats.length < 2) {
    return {
      result: NEW_LESION_RESPONSE.NOT_EVALUABLE,
      message: 'New Lesion(NE)',
    };
  }

  const current_index = stats.length - 1;
  const current = stats[current_index];
  const exist_new_lesion = current.existROIs;
  const exist_previous_lesion = stats.some(
    (s) => s.new_lesion_non_target_response === NEW_LESION_RESPONSE.iNN
  );

  if (exist_new_lesion) {
    return {
      result: NEW_LESION_RESPONSE.iNN,
      message: `New Lesion(Non-iCR/Non-iUPD): lesion found at ${current.id}; please refer to the DICOM images`,
    };
  }

  if (exist_previous_lesion) {
    return {
      result: NEW_LESION_RESPONSE.iCR,
      message: `New Lesion(iCR): no new lesion found at ${current.id}`,
    };
  }

  return {
    result: NEW_LESION_RESPONSE.ABSENT,
    message: 'New Lesion(ABSENT): no new lesion found',
  };
}

export function getImmuneOverallResponse(stats) {
  if (stats.length < 2) {
    return { result: TARGET_RESPONSE.NOT_EVALUABLE, message: '' };
  }

  const current_index = stats.length - 1;
  const stat = stats[current_index];
  /** progressive disease */
  if (stat.target_response === TARGET_RESPONSE.iCPD) {
    return { result: OVERALL_RESPONSE.iCPD, message: '' };
  }
  if (stat.non_target_response === NON_TARGET_RESPONSE.iCPD) {
    return { result: OVERALL_RESPONSE.iCPD, message: '' };
  }
  if (stat.new_lesion_target_response === NEW_LESION_RESPONSE.iCPD) {
    return { result: OVERALL_RESPONSE.iCPD, message: '' };
  }
  if (stat.clinical_status === CLINICAL_STATUS.WORSE) {
    return { result: OVERALL_RESPONSE.iCPD, message: '' };
  }

  /** evidence of progression */
  let previous_index = current_index - 1;
  while (stats[previous_index].overall_response === OVERALL_RESPONSE.iCPD) {
    previous_index += -1;
  }
  const previous = stats[previous_index];
  if (previous.overall_response === OVERALL_RESPONSE.iUPD) {
    if (previous.target_response === TARGET_RESPONSE.iUPD) {
      if (
        previous.non_target_response !== NON_TARGET_RESPONSE.iCPD &&
        previous.non_target_response !== NON_TARGET_RESPONSE.iUPD &&
        stat.non_target_response === NON_TARGET_RESPONSE.iUPD
      ) {
        return { result: OVERALL_RESPONSE.iCPD, message: '' };
      }
      if (
        previous.new_lesion_target_response !== NEW_LESION_RESPONSE.iCPD &&
        previous.new_lesion_target_response !== NEW_LESION_RESPONSE.iUPD &&
        stat.new_lesion_target_response === NEW_LESION_RESPONSE.iUPD
      ) {
        return { result: OVERALL_RESPONSE.iCPD, message: '' };
      }
    }
    if (previous.non_target_response === NON_TARGET_RESPONSE.iUPD) {
      if (
        previous.target_response !== TARGET_RESPONSE.iCPD &&
        previous.target_response !== TARGET_RESPONSE.iUPD &&
        stat.target_response === TARGET_RESPONSE.iUPD
      ) {
        return { result: OVERALL_RESPONSE.iCPD, message: '' };
      }
      if (
        previous.new_lesion_target_response !== NEW_LESION_RESPONSE.iCPD &&
        previous.new_lesion_target_response !== NEW_LESION_RESPONSE.iUPD &&
        stat.new_lesion_response === NEW_LESION_RESPONSE.iUPD
      ) {
        return { result: OVERALL_RESPONSE.iCPD, message: '' };
      }
    }
    if (previous.new_lesion_target_response === NEW_LESION_RESPONSE.iUPD) {
      if (
        previous.target_response !== TARGET_RESPONSE.iCPD &&
        previous.target_response !== TARGET_RESPONSE.iUPD &&
        stat.target_response === TARGET_RESPONSE.iUPD
      ) {
        return { result: OVERALL_RESPONSE.iCPD, message: '' };
      }
      if (
        previous.non_target_response !== NON_TARGET_RESPONSE.iCPD &&
        previous.non_target_response !== NON_TARGET_RESPONSE.iUPD &&
        stat.non_target_response === NON_TARGET_RESPONSE.iUPD
      ) {
        return { result: OVERALL_RESPONSE.iCPD, message: '' };
      }
    }
  }

  /** evidence of response */
  if (
    previous.non_target_response === NON_TARGET_RESPONSE.iUPD ||
    previous.new_lesion_target_response === NEW_LESION_RESPONSE.iUPD
  ) {
    if (
      stat.target_response === TARGET_RESPONSE.iCR ||
      stat.target_response === TARGET_RESPONSE.iPR
    ) {
      return { result: OVERALL_RESPONSE.iPR, message: '' };
    }
    if (stat.target_response === TARGET_RESPONSE.iSD) {
      return { result: OVERALL_RESPONSE.iSD, message: '' };
    }
  }

  /** unconfirmed progressive disease */
  if (stat.target_response === TARGET_RESPONSE.iUPD) {
    return { result: OVERALL_RESPONSE.iUPD, message: '' };
  }
  if (stat.non_target_response === NON_TARGET_RESPONSE.iUPD) {
    return { result: OVERALL_RESPONSE.iUPD, message: '' };
  }
  if (stat.new_lesion_target_response === NEW_LESION_RESPONSE.iUPD) {
    return { result: OVERALL_RESPONSE.iUPD, message: '' };
  }

  /** complete response */
  if (
    stat.target_response === TARGET_RESPONSE.iCR &&
    (stat.non_target_response === NON_TARGET_RESPONSE.iCR ||
      stat.non_target_response === NON_TARGET_RESPONSE.NONE) &&
    (stat.new_lesion_target_response === NEW_LESION_RESPONSE.iCR ||
      stat.new_lesion_target_response === NEW_LESION_RESPONSE.ABSENT)
  ) {
    return { result: OVERALL_RESPONSE.iCR, message: '' };
  }
  if (
    stat.target_response === TARGET_RESPONSE.NONE &&
    stat.non_target_response === NON_TARGET_RESPONSE.iCR &&
    (stat.new_lesion_target_response === NEW_LESION_RESPONSE.iCR ||
      stat.new_lesion_target_response === NEW_LESION_RESPONSE.ABSENT)
  ) {
    return { result: OVERALL_RESPONSE.iCR, message: '' };
  }

  /** partial response */
  if (
    stat.target_response === TARGET_RESPONSE.iCR ||
    stat.target_response === TARGET_RESPONSE.iPR
  ) {
    return { result: OVERALL_RESPONSE.iPR, message: '' };
  }

  /** stable disease */
  if (stat.target_response === TARGET_RESPONSE.iSD) {
    return { result: OVERALL_RESPONSE.iSD, message: '' };
  }
  if (
    stat.target_response === TARGET_RESPONSE.NONE &&
    stat.non_target_response === NON_TARGET_RESPONSE.iNN
  ) {
    return { result: OVERALL_RESPONSE.iSD, message: '' };
  }

  /** not evaluable */
  if (
    stat.target_response === TARGET_RESPONSE.NOT_EVALUABLE ||
    stat.non_target_response === NON_TARGET_RESPONSE.NOT_EVALUABLE
  ) {
    return { result: OVERALL_RESPONSE.NOT_EVALUABLE, message: '' };
  }

  /** none response */
  return { result: OVERALL_RESPONSE.NONE, message: '' };
}
